import React, { JSX, useEffect, useState } from 'react'
import classNames from 'classnames'
import { TriviaSummary } from 'types/api'

export const DEFAULT_TRIVIA_GRACE_SECONDS = 5

export const TRIVIA_REVEAL_NOW_AFTER_MS = 10_000

const parseMs = (iso: string | null | undefined): number =>
  iso != null ? Date.parse(iso) : Number.NaN

export const triviaYouTubeCounting = (trivia: TriviaSummary, nowMs: number): boolean => {
  if (trivia.closedAt == null || trivia.youtubeFinalAt != null) return false
  const until = parseMs(trivia.youtubeCountingUntil)
  return Number.isFinite(until) && nowMs < until
}

export const triviaYouTubeTimedOut = (trivia: TriviaSummary, nowMs: number): boolean => {
  if (trivia.closedAt == null || trivia.youtubeFinalAt != null) return false
  const until = parseMs(trivia.youtubeCountingUntil)
  return Number.isFinite(until) && nowMs >= until
}

export type TriviaPhase = 'draft' | 'live' | 'grace' | 'counting' | 'locked' | 'revealed'

export const triviaPhaseOf = (trivia: TriviaSummary, nowMs: number): TriviaPhase => {
  if (trivia.revealedAt != null) return 'revealed'
  if (trivia.closedAt != null) {
    const graceEnd = parseMs(trivia.graceEndsAt)
    if (Number.isFinite(graceEnd) && nowMs < graceEnd) return 'grace'
    return triviaYouTubeCounting(trivia, nowMs) ? 'counting' : 'locked'
  }
  return trivia.status === 'ACTIVE' ? 'live' : 'draft'
}

export const triviaRevealArmed = (trivia: TriviaSummary, nowMs: number): boolean => {
  if (trivia.revealedAt != null) return false
  if (trivia.closedAt != null) return triviaPhaseOf(trivia, nowMs) === 'locked'
  return trivia.status === 'INACTIVE' && trivia.startedAt != null && trivia.stoppedAt != null
}

export const TRIVIA_PHASE_STYLES: Record<TriviaPhase, { label: string, text: string, dot: string }> = {
  draft: { label: 'Draft', text: 'text-zinc-400', dot: 'bg-zinc-400' },
  live: { label: 'Live', text: 'text-emerald-300', dot: 'bg-emerald-400' },
  grace: { label: 'Grace', text: 'text-amber-300', dot: 'bg-amber-400' },
  counting: { label: 'Counting YouTube', text: 'text-cyan-300', dot: 'bg-cyan-400 animate-pulse' },
  locked: { label: 'Locked', text: 'text-sky-300', dot: 'bg-sky-400' },
  revealed: { label: 'Revealed', text: 'text-fuchsia-300', dot: 'bg-fuchsia-400' }
}

const TIMED_OUT_STYLE = {
  label: 'Locked: YouTube timed out, some answers may be missing',
  text: 'text-orange-300',
  dot: 'bg-orange-400'
}
const TIMED_OUT_TOOLTIP = 'FlightDeck counts YouTube answers for up to about 2 minutes after the question locks, then stops waiting so Reveal is never held longer. This time the count was not done by then, so some YouTube answers sent in time may be missing. Only your team sees this; viewers never do.'

export const TRIVIA_COUNTING_TOOLTIP = 'FlightDeck is reading YouTube one more time so every answer sent before the question locked is counted. Usually a few seconds; FlightDeck waits at most about 2 minutes.'
export const TRIVIA_REVEAL_NOW_TOOLTIP = 'Skip the wait and reveal now. You are asked to confirm first.'
export const TRIVIA_STILL_COUNTING_LABEL = 'Still counting YouTube'
export const TRIVIA_STILL_COUNTING_TOOLTIP = 'You revealed before the YouTube count was final. YouTube answers sent before the question locked still count, so the YouTube numbers may go up once. This note goes away when the count is final.'
export const TRIVIA_REVEAL_NOW_CONFIRM = {
  title: 'Reveal now?',
  body: 'FlightDeck is still counting YouTube answers for this question. If you reveal now, answers sent before the question locked still count, so the YouTube numbers on screen may go up once after the reveal. Twitch numbers will not change.',
  confirmLabel: 'Reveal now',
  cancelLabel: 'Keep waiting'
}

export const triviaCountingElapsedMs = (trivia: TriviaSummary, nowMs: number): number | null => {
  if (triviaPhaseOf(trivia, nowMs) !== 'counting') return null
  const graceEnd = parseMs(trivia.graceEndsAt)
  return Number.isFinite(graceEnd) ? Math.max(0, nowMs - graceEnd) : null
}

export const triviaRevealNowOffered = (trivia: TriviaSummary, nowMs: number): boolean => {
  const elapsed = triviaCountingElapsedMs(trivia, nowMs)
  return elapsed != null && elapsed >= TRIVIA_REVEAL_NOW_AFTER_MS
}

export const triviaCountingButtonLabel = (trivia: TriviaSummary, nowMs: number): string => {
  const elapsed = triviaCountingElapsedMs(trivia, nowMs)
  if (elapsed == null || elapsed < TRIVIA_REVEAL_NOW_AFTER_MS) return 'Counting YouTube answers...'
  return `Counting YouTube answers... ${Math.floor(elapsed / 1000)} s`
}

export const triviaCountingTooltip = (trivia: TriviaSummary, nowMs: number): string =>
  triviaRevealNowOffered(trivia, nowMs) ? `${TRIVIA_COUNTING_TOOLTIP} Reveal now skips the wait.` : TRIVIA_COUNTING_TOOLTIP

export const triviaResetConfirmCopy = (label: string, revealed: boolean): string => {
  if (revealed) {
    return `Reset "${label}"?\n\n` +
      'This question is revealed. WARNING: resetting permanently DELETES every recorded answer for it, sets all counts back to zero and removes it from the leaderboard. It cannot be undone.\n\n' +
      'The question goes back to a startable draft with the same correct answer. To keep this round\'s results on the leaderboard, do not reset: duplicate the question and run the copy instead.\n\n' +
      'Delete all answers and reset?'
  }
  return `Reset "${label}"?\n\n` +
    'WARNING: this permanently DELETES every recorded answer for this question and sets all counts back to zero. It cannot be undone.\n\n' +
    'Delete all answers and reset?'
}

export const TriviaStatusChip = ({ phase, youtubeTimedOut = false }: {
  phase: TriviaPhase
  youtubeTimedOut?: boolean
}): JSX.Element => {
  const timedOut = phase === 'locked' && youtubeTimedOut
  const style = timedOut ? TIMED_OUT_STYLE : TRIVIA_PHASE_STYLES[phase]
  return (
    <span
      className={classNames('inline-flex items-center gap-1.5 text-[11px] font-medium', style.text)}
      title={timedOut ? TIMED_OUT_TOOLTIP : phase === 'counting' ? TRIVIA_COUNTING_TOOLTIP : undefined}
    >
      <span className={classNames('h-1.5 w-1.5 shrink-0 rounded-full', style.dot)} />
      {style.label}
    </span>
  )
}

export const useTriviaGraceTick = (trivias: TriviaSummary[]): number => {
  const [nowMs, setNowMs] = useState<number>(() => Date.now())
  const anyTicking = trivias.some((t) =>
    (t.revealedAt == null &&
      t.closedAt != null &&
      t.graceEndsAt != null &&
      Date.parse(t.graceEndsAt) > nowMs) ||
    triviaYouTubeCounting(t, nowMs)
  )
  useEffect(() => {
    if (!anyTicking) return
    const id = globalThis.setInterval(() => setNowMs(Date.now()), 1000)
    return () => globalThis.clearInterval(id)
  }, [anyTicking])
  return nowMs
}
